import { useSettings } from 'contexts/Settings/Settings';
import { getUrl } from 'helpers';
import { Howl } from 'howler';
import _ from 'lodash';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

const SoundsContext = createContext<SoundsState>({} as SoundsState);

export const useSounds = () => useContext(SoundsContext);

export function withSounds(WrappedComponent: any) {
    return function SoundsWrappedComponent(props: any) {
        return (
            <SoundsContext.Consumer>{state => <WrappedComponent {...props} sounds={state} />}</SoundsContext.Consumer>
        );
    };
}

export function SoundsProvider({ children }: SoundsProviderProps) {
    const [cache, setCache] = useState<SoundsCache>({});
    const [interacted, setInteracted] = useState(false);
    const [muted, setMuted] = useLocalStorage('sounds.muted', false);
    const settings = useSettings();
    const interactedRef = useRef(interacted);
    interactedRef.current = interacted;
    const mutedRef = useRef(muted);
    mutedRef.current = muted;

    const handleInteraction = useCallback(() => {
        // TODO: Check for a11y
        setInteracted(true);
    }, []);

    useEffect(() => {
        window.addEventListener('keydown', handleInteraction);
        window.addEventListener('pointerup', handleInteraction);

        return () => {
            window.removeEventListener('keydown', handleInteraction);
            window.removeEventListener('pointerup', handleInteraction);
        };
    }, [handleInteraction]);

    useEffect(() => {
        // Update cached sounds mute state
        Object.values(cache).forEach(sound => {
            sound.volume(settings.volume);
            sound.mute(muted);
        });
    }, [cache, muted, settings.volume]);

    function play(soundName: SoundName, volume = 1): Howl | null {
        // Check interacted and mute states
        if (!interactedRef.current || mutedRef.current) {
            return null;
        }

        // Get sound from cache
        let sound = cache[soundName];

        if (!sound) {
            // Create sound
            sound = new Howl({
                autoplay: true,
                src: [getUrl(`/assets/sounds/${soundName}.mp3`)],
            });

            // Store in cache
            setCache(prevCache => ({
                ...prevCache,
                [soundName]: sound,
            }));
        }

        // Play sound
        sound.volume(settings.volume * _.clamp(volume, 0, 1));
        sound.play();

        return sound;
    }

    const state: SoundsState = {
        muted,
        play,
        setMuted,
    };

    return <SoundsContext.Provider value={state}>{children}</SoundsContext.Provider>;
}
